import React, { FC, useEffect, useState } from 'react'
import isAuthenticated from '../../routes/PrivateRoute'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Tab from 'react-bootstrap/Tab'
import Nav from 'react-bootstrap/Nav'
import Container from 'react-bootstrap/Container'
import { useGlobalState } from '../../hooks/store/useGlobalState'
import { useHistory, useParams } from 'react-router-dom'
import { OrganizationDetail, OrganizationList } from '../../components/FhirData/FhirResources/Organization'
import { PractitionerDetail, PractitionerList } from '../../components/FhirData/FhirResources/Practitioner'
import { InsurancePlanDetail, InsurancePlanList } from '../../components/FhirData/FhirResources/InsurancePlan'
import { MedicationKnowledgeDetail, MedicationKnowledgeList } from '../../components/FhirData/FhirResources/MedicationKnowledge'
import { NetworkList, NetworkDetail } from '../../components/FhirData/FhirResources/Network'
import { LocationDetail, LocationList } from '../../components/FhirData/FhirResources/Location'
import PermissionGate from '../../components/PermissionGate'
import useUserWithRole from '../../hooks/authentication/useUserWithRole'

import { scrollToTop } from '../../utils/helpers'
import usePaginatedProviderFhirResource from '../../hooks/admin/usePaginatedProviderResource'
import HealthcareServiceDetail from '../../components/FhirData/FhirResources/HealthcareService/HealthcareServiceDetail'
import HealthcareServiceList from '../../components/FhirData/FhirResources/HealthcareService/HealthcareServiceList'
import OrganizationAffiliationDetail from '../../components/FhirData/FhirResources/OrganizationAffiliation/OrganizationAffiliationDetail'
import OrganizationAffiliationList from '../../components/FhirData/FhirResources/OrganizationAffiliation/OrganizationAffiliationList'
import PractitionerRoleDetail from '../../components/FhirData/FhirResources/PractitionerRole/PractitionerRoleDetail'
import PractitionerRoleList from '../../components/FhirData/FhirResources/PractitionerRole/PractitionerRoleList'
import { Endpoint, MedicationKnowledge } from '@teschglobal/tg-fhir-react-components/dist/types'
import FormularyCoveragePlanList from '../../components/FhirData/FhirResources/FormularyCoveragePlan/FormularyCoveragePlanList'
import { List } from '@teschglobal/tg-fhir-react-components/dist/types'
import FormularyCoveragePlanDetail from '../../components/FhirData/FhirResources/FormularyCoveragePlan/FormularyCoveragePlanDetail'
import { appConfig } from '../../assets/customizations/config'
import PageTitle from '../../components/PageTitle'
import { Location, Organization, OrganizationAffiliation, Practitioner, InsurancePlan, PractitionerRole, HealthcareService } from '@teschglobal/tg-fhir-react-components/dist/types'
import { EndpointDetail, EndpointList } from '../../components/FhirData/FhirResources/Endpoint'

type RouteParams = {
    page: string | undefined
    resourceId: string | undefined
}

const getKeys = () => {
    const keys = [
        { resource: 'Endpoint', label: 'Endpoint' },
        { resource: 'Organization', label: 'Organization' },
        { resource: 'OrganizationAffiliation', label: 'OrganizationAffiliation' },
        { resource: 'Practitioner', label: 'Practitioner' },
        { resource: 'PractitionerRole', label: 'PractitionerRole' },
        { resource: 'HealthcareService', label: 'HealthcareService' },
        { resource: 'InsurancePlan', label: 'InsurancePlan' },
        { resource: 'Location', label: 'Location' },
        { resource: 'Network', label: 'Network' },
        { resource: 'MedicationKnowledge', label: 'MedicationKnowledge' },
        { resource: 'List', label: 'List (Formulary Coverage Plans)' }
    ]
    return { keys }
}

const ProviderList: FC = () => {
    //Extract state and setter functions from global state
    const {
        providerResource,
        setProviderResource,
        providerSearchTerms,
        setProviderSearchTerms,
        setProvider,
        setOrganization,
        setOrganizationAffiliation,
        setInsurancePlan,
        setPractitionerRole,
        provider,
        organization,
        organizationAffiliation,
        insurancePlan,
        practitionerRole,
        setLocation,
        location,
        setMedicationKnowledge,
        medicationKnowledge,
        list,
        setList,
        setHealthcareService,
        endpoint,
        setEndpoint,
    } = useGlobalState((state) => ({
        providerResource: state.providerResource,
        providerSearchTerms: state.providerSearchTerms,
        setProviderSearchTerms: state.setProviderSearchTerms,
        setProvider: state.setProvider,
        setOrganization: state.setOrganization,
        setOrganizationAffiliation: state.setOrganizationAffiliation,
        setInsurancePlan: state.setInsurancePlan,
        setPractitionerRole: state.setPractitionerRole,
        provider: state.provider,
        organization: state.organization,
        organizationAffiliation: state.organizationAffiliation,
        insurancePlan: state.insurancePlan,
        practitionerRole: state.practitionerRole,
        setProviderResource: state.setProviderResource,
        location: state.location,
        setLocation: state.setLocation,
        medicationKnowledge: state.medicationKnowledge,
        setMedicationKnowledge: state.setMedicationKnowledge,
        list: state.list,
        setList: state.setList,
        setHealthcareService: state.setHealthcareService,
        endpoint: state.endpoint,
        setEndpoint: state.setEndpoint,
    }))

    const { keys } = getKeys()
    const [key, setKey] = useState(providerResource)

    // Gets FHIR resourceID for detail from URL
    const { page, resourceId } = useParams<RouteParams>()

    const history = useHistory()

    const handleChangeKey = (k: string | null) => {
        setKey(k || '')
        setProviderResource(k || '')
        handleClearForm()

        newSearch()

        if (k) {
            const path = `/provider-list/${k}`
            history.push(path)
        }
        scrollToTop()
    }

    const { user } = useUserWithRole()

    const {
        refetch: fetchProviderResource,
        data: providerResourceData,
        isLoading: providerResourceIsLoading,
        isSuccess: providerResourceIsSuccess,
        isFetching: providerResourceIsFetching,
        totalPages: providerResourceTotalPages,
        currentPage: providerResourceCurrentPage,
        handleChangePage: handleChangePageProviderResource,
        handleNewSearch: handleNewSearchProviderResource,
    } = usePaginatedProviderFhirResource({
        resource: key,
        searchTerms: providerSearchTerms
    }, 25)

    useEffect(() => {
        fetchProviderResource()
    }, [key, providerSearchTerms, resourceId])

    const newSearch = () => {
        handleNewSearchProviderResource()
    }

    const handleLinkClick = (resource: Endpoint | Organization | Practitioner | PractitionerRole | InsurancePlan | Location | OrganizationAffiliation | MedicationKnowledge | List | HealthcareService) => {

        if (key === 'Organization' || key === 'Network') {
            setOrganization(resource as Organization)
        }

        if (key === 'Practitioner') {
            setProvider(resource as Practitioner)
        }
        if (key === 'PractitionerRole') {
            setPractitionerRole(resource as PractitionerRole)
        }

        if (key === 'InsurancePlan') {
            setInsurancePlan(resource as InsurancePlan)
        }
        if (key === 'Location') {
            setLocation(resource as Location)
        }
        if (key === 'OrganizationAffiliation') {
            setOrganizationAffiliation(resource as OrganizationAffiliation)
        }

        if (key === 'HealthcareService') {
            setHealthcareService(resource as HealthcareService)
        }

        if (key === 'MedicationKnowledge') {
            setMedicationKnowledge(resource as MedicationKnowledge)
        }

        if (key === 'List') {
            setList(resource as List)
        }

        if (key === 'Endpoint') {
            setEndpoint(resource as Endpoint)
        }

        if (key) {
            const path = `/provider-list/${key}/${resource.id}`
            history.push(path)
        }

        scrollToTop()
    }

    useEffect(() => {
        if (page) {
            const key = keys.find((k) => k.resource === page)
            if (key) {
                setKey(key.resource)
            } else {
                history.replace('/404')
            }
        }
        scrollToTop()
    }, [page])

    const resourceType = key

    const keyObj = keys.find(k => k.resource === key)
    const displayName = keyObj ? keyObj.label : key

    const handleClearForm = () => {
        setProviderSearchTerms('')
        newSearch()
    }

    const handleUpdateSearchTerms = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProviderSearchTerms(e.target.value)
    }

    const selectedService: HealthcareService | null = providerResourceData?.data?.find(
        (s: HealthcareService) => s.id === resourceId
    )

    return (
        <section>
            <PermissionGate
                user={user}
                role='admin'
            >
                <Container>
                    <Tab.Container activeKey={key} onSelect={handleChangeKey} transition={false}>
                        <PageTitle
                            resourceType={resourceType}
                            displayName={displayName}
                        />
                        <h1 className='headerText h2'>Provider Directory API</h1>
                        <Row>
                            <Col sm={3}>
                                <div className='qa-tools-label-2'>
                                    <h2 className='qa-tools-header h6'>QA TOOLS</h2>
                                    <Nav
                                        variant="pills" className="flex-column qa-tools-nav">
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="Endpoint" className="qa-tools-nav-link">
                                                Endpoint
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="Organization" className="qa-tools-nav-link">
                                                Organization
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="OrganizationAffiliation" className="qa-tools-nav-link">
                                                OrganizationAffiliation
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="Practitioner" className="qa-tools-nav-link">
                                                Practitioner
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="PractitionerRole" className="qa-tools-nav-link">
                                                PractitionerRole
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="HealthcareService" className="qa-tools-nav-link">
                                                HealthcareService
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="InsurancePlan" className="qa-tools-nav-link">
                                                InsurancePlan
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="Location" className="qa-tools-nav-link">
                                                Location
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item className="patient-pill">
                                            <Nav.Link eventKey="Network" className="qa-tools-nav-link">
                                                Network
                                            </Nav.Link>
                                        </Nav.Item>
                                        {appConfig.use_formulary && (
                                            <>
                                                <Nav.Item className="patient-pill">
                                                    <Nav.Link eventKey="MedicationKnowledge" className="qa-tools-nav-link">
                                                        MedicationKnowledge
                                                    </Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item className='patient-pill'>
                                                    <Nav.Link eventKey="List" className='qa-tools-nav-link'>
                                                        List (Formulary Coverage Plans)
                                                    </Nav.Link>
                                                </Nav.Item>
                                            </>
                                        )}
                                    </Nav>
                                </div>
                            </Col>
                            <Col sm={9} className='mt-sm-0 mt-3'>
                                <Tab.Content>
                                    <Tab.Pane eventKey='Endpoint'>
                                        {
                                            key === 'Endpoint' && (
                                                <Tab.Content>
                                                    {
                                                        resourceId ? (
                                                            <EndpointDetail resource={endpoint} isFetching={providerResourceIsFetching} />
                                                        ) : (
                                                            <EndpointList
                                                                endpoints={providerResourceData}
                                                                clickViewDataLink={handleLinkClick}
                                                                isFetching={providerResourceIsFetching}
                                                                isLoading={providerResourceIsLoading}
                                                                isSuccess={providerResourceIsSuccess}
                                                                totalPages={providerResourceTotalPages}
                                                                currentPage={providerResourceCurrentPage}
                                                                handleChangePage={handleChangePageProviderResource}
                                                            />
                                                        )
                                                    }
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                    <Tab.Pane eventKey='Organization'>
                                        {
                                            key === 'Organization' && (
                                                <Tab.Content>
                                                    {
                                                        resourceId ? (
                                                            <OrganizationDetail resource={organization} isFetching={providerResourceIsFetching} />
                                                        ) : (
                                                            <OrganizationList
                                                                organizations={providerResourceData}
                                                                clickViewDataLink={handleLinkClick}
                                                                searchProviderResource={{ searchTerms: providerSearchTerms, resource: '' }}
                                                                isFetching={providerResourceIsFetching}
                                                                isLoading={providerResourceIsLoading}
                                                                isSuccess={providerResourceIsSuccess}
                                                                clearForm={handleClearForm}
                                                                newSearch={newSearch}
                                                                updateSearchTerms={handleUpdateSearchTerms}
                                                                totalPages={providerResourceTotalPages}
                                                                currentPage={providerResourceCurrentPage}
                                                                handleChangePage={handleChangePageProviderResource}
                                                            />

                                                        )
                                                    }
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>

                                    <Tab.Pane eventKey='OrganizationAffiliation'>
                                        {
                                            key === 'OrganizationAffiliation' && (
                                                <Tab.Content>
                                                    {
                                                        resourceId ? (
                                                            <OrganizationAffiliationDetail resource={organizationAffiliation} isFetching={providerResourceIsFetching} />
                                                        ) : (
                                                            <OrganizationAffiliationList
                                                                organizationAffiliations={providerResourceData}
                                                                clickViewDataLink={handleLinkClick}
                                                                isFetching={providerResourceIsFetching}
                                                                isSuccess={providerResourceIsSuccess}
                                                                totalPages={providerResourceTotalPages}
                                                                currentPage={providerResourceCurrentPage}
                                                                handleChangePage={handleChangePageProviderResource}
                                                            />
                                                        )
                                                    }
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                    <Tab.Pane eventKey='Practitioner'>
                                        {
                                            key === 'Practitioner' && (
                                                <Tab.Content>
                                                    {
                                                        resourceId ? (
                                                            <PractitionerDetail
                                                                resource={provider}
                                                                isFetching={providerResourceIsFetching} />
                                                        ) : (
                                                            <PractitionerList
                                                                practitioners={providerResourceData}
                                                                clickViewDataLink={handleLinkClick}
                                                                searchProviderResource={{ searchTerms: providerSearchTerms, resource: '' }}
                                                                isFetching={providerResourceIsFetching}
                                                                isLoading={providerResourceIsLoading}
                                                                isSuccess={providerResourceIsSuccess}
                                                                clearForm={handleClearForm}
                                                                newSearch={newSearch}
                                                                updateSearchTerms={handleUpdateSearchTerms}
                                                                totalPages={providerResourceTotalPages}
                                                                currentPage={providerResourceCurrentPage}
                                                                handleChangePage={handleChangePageProviderResource}
                                                            />
                                                        )
                                                    }
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                    <Tab.Pane eventKey='PractitionerRole'>
                                        {
                                            key === 'PractitionerRole' && (
                                                <Tab.Content>
                                                    {
                                                        resourceId ? (
                                                            <PractitionerRoleDetail resource={practitionerRole} isFetching={providerResourceIsFetching} />
                                                        ) : (
                                                            <PractitionerRoleList
                                                                practitionersRole={providerResourceData}
                                                                clickViewDataLink={handleLinkClick}
                                                                isFetching={providerResourceIsFetching}
                                                                isLoading={providerResourceIsLoading}
                                                                isSuccess={providerResourceIsSuccess}
                                                                totalPages={providerResourceTotalPages}
                                                                currentPage={providerResourceCurrentPage}
                                                                handleChangePage={handleChangePageProviderResource}
                                                            />
                                                        )
                                                    }
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                    <Tab.Pane eventKey='HealthcareService'>
                                        {
                                            key === 'HealthcareService' && (
                                                <Tab.Content>
                                                    {resourceId ? (
                                                        <HealthcareServiceDetail service={selectedService} />
                                                    ) : (
                                                        <HealthcareServiceList
                                                            services={providerResourceData}
                                                            clickViewDataLink={handleLinkClick}
                                                            isFetching={providerResourceIsFetching}
                                                            isLoading={providerResourceIsLoading}
                                                            isSuccess={providerResourceIsSuccess}
                                                            totalPages={providerResourceTotalPages}
                                                            currentPage={providerResourceCurrentPage}
                                                            handleChangePage={handleChangePageProviderResource}
                                                        />
                                                    )}
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="InsurancePlan">
                                        {
                                            key === 'InsurancePlan' && (
                                                <Tab.Content>
                                                    {resourceId ? (
                                                        <InsurancePlanDetail resource={insurancePlan} />
                                                    ) : (
                                                        <InsurancePlanList
                                                            insurancePlans={providerResourceData}
                                                            clickViewDataLink={handleLinkClick}
                                                            isFetching={providerResourceIsFetching}
                                                            isLoading={providerResourceIsLoading}
                                                            isSuccess={providerResourceIsSuccess}
                                                            totalPages={providerResourceTotalPages}
                                                            currentPage={providerResourceCurrentPage}
                                                            handleChangePage={handleChangePageProviderResource}
                                                        />
                                                    )}
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                    <Tab.Pane eventKey='Location'>
                                        {
                                            key === 'Location' && (
                                                <Tab.Content>
                                                    {
                                                        resourceId ? (
                                                            <LocationDetail resource={location} />
                                                        ) : (
                                                            <LocationList
                                                                locations={providerResourceData}
                                                                clickViewDataLink={handleLinkClick}
                                                                searchProviderResource={{ searchTerms: providerSearchTerms, resource: '' }}
                                                                isFetching={providerResourceIsFetching}
                                                                isSuccess={providerResourceIsSuccess}
                                                                clearForm={handleClearForm}
                                                                newSearch={newSearch}
                                                                updateSearchTerms={handleUpdateSearchTerms}
                                                                totalPages={providerResourceTotalPages}
                                                                currentPage={providerResourceCurrentPage}
                                                                handleChangePage={handleChangePageProviderResource}
                                                            />
                                                        )
                                                    }
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane >

                                    <Tab.Pane eventKey="Network">
                                        {
                                            key === 'Network' && (
                                                <Tab.Content>
                                                    {resourceId ? (
                                                        <NetworkDetail networks={organization} />
                                                    ) : (
                                                        <NetworkList
                                                            networks={providerResourceData}
                                                            clickViewDataLink={handleLinkClick}
                                                            isFetching={providerResourceIsFetching}
                                                            isLoading={providerResourceIsLoading}
                                                            isSuccess={providerResourceIsSuccess}
                                                            totalPages={providerResourceTotalPages}
                                                            currentPage={providerResourceCurrentPage}
                                                            handleChangePage={handleChangePageProviderResource}
                                                        />
                                                    )}
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="MedicationKnowledge">
                                        {key === 'MedicationKnowledge' && (
                                            <Tab.Content>
                                                {resourceId ? (
                                                    <MedicationKnowledgeDetail resource={medicationKnowledge} />
                                                ) : (
                                                    <MedicationKnowledgeList
                                                        medicationKnowledges={providerResourceData}
                                                        clickViewDataLink={handleLinkClick}
                                                        isFetching={providerResourceIsFetching}
                                                        isLoading={providerResourceIsLoading}
                                                        isSuccess={providerResourceIsSuccess}
                                                        totalPages={providerResourceTotalPages}
                                                        currentPage={providerResourceCurrentPage}
                                                        handleChangePage={handleChangePageProviderResource}
                                                    />
                                                )}
                                            </Tab.Content>
                                        )}
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="List">
                                        {
                                            key === 'List' && (
                                                <Tab.Content>
                                                    {resourceId ? (
                                                        <FormularyCoveragePlanDetail
                                                            resource={list}
                                                            isFetching={providerResourceIsFetching}
                                                        />
                                                    ) : (
                                                        <FormularyCoveragePlanList
                                                            coveragePlans={providerResourceData}
                                                            clickViewDataLink={handleLinkClick}
                                                            isFetching={providerResourceIsFetching}
                                                            isLoading={providerResourceIsLoading}
                                                            isSuccess={providerResourceIsSuccess}
                                                            totalPages={providerResourceTotalPages}
                                                            currentPage={providerResourceCurrentPage}
                                                            handleChangePage={handleChangePageProviderResource}
                                                        />
                                                    )}
                                                </Tab.Content>
                                            )
                                        }
                                    </Tab.Pane>
                                </Tab.Content >
                            </Col >
                        </Row >
                    </Tab.Container >
                </Container >
            </PermissionGate >
        </section >
    )
}

export default isAuthenticated(ProviderList)